import { useEffect, useState } from 'react';
import tw from 'twin.macro';
import { useTranslation } from 'react-i18next';

import { Dropdown, IDropdownOption, Option } from '@components/Dropdown';
import {
  SegmentCreatorData,
  useSegmentCreatorContext,
} from '@insights_module/context/SegmentCreatorContext';
import { AQLQueryCondition, AQLQueryType } from '@insights_module/adc_ql/types';
import { FilterInput } from '@insights_module/components/FilterInput';
import { useFilterTypeOptions } from '@insights_module/hooks/useFilterTypeOptions';

export const SegmentCreatorEpisodesByShowIdItem = ({
  segmentData,
}: {
  segmentData: SegmentCreatorData;
}) => {
  const { condition, value, operator } = segmentData;
  const { t } = useTranslation();
  const { pushSegmentData, isPresentational } = useSegmentCreatorContext();
  const { dropdownOptions } = useFilterTypeOptions();

  const conditionOptions: IDropdownOption[] = [
    {
      value: 'eq',
      label: <Option>{t('insights.query_builder.conditions.is')}</Option>,
    },
  ];

  const [selectedType, setSelectedType] = useState<IDropdownOption>(
    dropdownOptions.find((item) => item.value === segmentData.type) || dropdownOptions[0]
  );
  const [selectedCondition, setSelectedCondition] = useState<IDropdownOption>(
    conditionOptions.find(
      (item) => item.value === (condition as unknown as string) && item.meta?.operator === operator
    ) || conditionOptions[0]
  );
  const [selectedShowId, setSelectedShowId] = useState<string>((value as string) || '');

  useEffect(() => {
    if (selectedType) {
      const type = selectedType.value as string;
      pushSegmentData({
        ...segmentData,
        type: type as AQLQueryType,
        category: null,
        value: null,
        condition: null,
        negation: false,
      });
    }
  }, [selectedType]);

  useEffect(() => {
    if (selectedCondition && selectedShowId) {
      const segment = {
        ...segmentData,
        condition: selectedCondition.value as unknown as Extract<
          AQLQueryCondition,
          'eq' | 'any_of'
        >,
        value: selectedShowId,
      };
      pushSegmentData(segment);
    }
  }, [selectedCondition, selectedShowId]);

  return (
    <div tw='flex gap-1 items-center'>
      <Dropdown
        options={dropdownOptions}
        value={selectedType}
        onChange={(val) => setSelectedType(val as IDropdownOption)}
        labelStyle={tw`bg-spec-episode-title-filter-bg border border-spec-episode-title-filter-stroke text-spec-episode-title-filter-text font-semibold`}
        caretStyle={tw`fill-spec-episode-title-filter-text`}
        dropdownMenuStyle={tw`w-[178px]`}
        isPresentational={isPresentational}
      />

      <Dropdown
        options={conditionOptions}
        value={selectedCondition}
        onChange={(val) => setSelectedCondition(val as IDropdownOption)}
        isPresentational={isPresentational}
      />

      <FilterInput
        focus
        value={selectedShowId}
        contentEditable={!isPresentational}
        placeholder={t('insights.query_builder.episodes_by_show_id.placeholder')}
        onPressEnter={(value) => setSelectedShowId(value as string)}
      />
    </div>
  );
};
